"use client";

import React, { useState, useEffect, ReactNode } from "react";
import { Collapse, Text, Badge, Flex, Box, MantineBreakpointsValues, Divider } from "@mantine/core";
import { TbChevronRight, TbChevronDown } from "react-icons/tb";

interface CollapsibleProps {
  label: string;
  count?: number;
  icon?: ReactNode;
  children: ReactNode;
  defaultExpanded?: boolean;
  expanded?: boolean; // Controlled state
  onToggle?: (expanded: boolean) => void;
  onClick?: () => void;
  isSelected?: boolean;
  depth?: number;
  color?: string;
  p?: number | Partial<MantineBreakpointsValues>;
}

export function Collapsible({
  label,
  count,
  icon,
  children,
  defaultExpanded = false,
  expanded,
  onToggle,
  onClick,
  isSelected = false,
  depth = 0,
  color = "gray",
  p = {base: "2", sm: "3"},
}: CollapsibleProps) {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

  // Single helper to get responsive style props (convert px to rem)
  const getResponsiveStyleProps = (padding: number | Partial<MantineBreakpointsValues>, multiplier: number = 1) => {
    if (typeof padding === 'number') {
      return `${(padding * multiplier) * 0.25}rem`;
    }
    // For responsive padding, create responsive values with rem conversion
    const { base, xs, sm, md, lg, xl } = padding;
    return {
      base: base ? `${(Number(base) * multiplier) * 0.25}rem` : undefined,
      xs: xs ? `${(Number(xs) * multiplier) * 0.25}rem` : undefined,
      sm: sm ? `${(Number(sm) * multiplier) * 0.25}rem` : undefined,
      md: md ? `${(Number(md) * multiplier) * 0.25}rem` : undefined,
      lg: lg ? `${(Number(lg) * multiplier) * 0.25}rem` : undefined,
      xl: xl ? `${(Number(xl) * multiplier) * 0.25}rem` : undefined,
    }
  };

  // Use controlled state if provided, otherwise use internal state
  const isExpanded = expanded !== undefined ? expanded : internalExpanded;

  // Update internal state when defaultExpanded changes (for controlled mode)
  useEffect(() => {
    if (expanded === undefined) {
      setInternalExpanded(defaultExpanded);
    }
  }, [defaultExpanded, expanded]);

  const handleClick = () => {
    const newExpanded = !isExpanded;

    if (expanded === undefined) {
      // Uncontrolled mode
      setInternalExpanded(newExpanded);
    }

    onToggle?.(newExpanded);
    onClick?.();
  };

  // Get responsive style values
  const paddingValue = getResponsiveStyleProps(p, 1); // multiplier 1 for padding
  const leftValue = getResponsiveStyleProps(p, 1); // multiplier 1 for left positioning
  const paddingLeftValue = getResponsiveStyleProps(p, 2); // multiplier 2 for content padding

  return (
    <Box>
      {/* Clickable header */}
      <Flex
        className={`items-center gap-3 fs-hover-surface transition-colors duration-150 cursor-pointer rounded-md ${
          isSelected ? "bg-blue-light" : ""
        }`}
        p={paddingValue}
        onClick={handleClick}
      >
        {/* Chevron */}
        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
          {isExpanded ? (
            <TbChevronDown size={14} className="text-gray-500" />
          ) : (
            <TbChevronRight size={14} className="text-gray-500" />
          )}
        </div>

        {/* Icon */}
        {icon && <div className="flex-shrink-0">{icon}</div>}

        {/* Label */}
        <Text
          size="md"
          fw={depth === 0 ? 600 : 500}
          className={`flex-1 truncate ${
            isSelected ? "text-blue-light-color" : ""
          }`}
        >
          {label}
        </Text>

        {/* Count badge */}
        {count !== undefined && (
          <Badge
            size="sm"
            variant="light"
            color={color}
            className="flex-shrink-0"
          >
            {count}
          </Badge>
        )}
      </Flex>

      {/* Collapsible content */}
      <Collapse in={isExpanded} className="relative">
        {/* Vertical lines for hierarchy - show one line for each level */}
        <Divider
          className="absolute h-full top-0 bottom-0"
          orientation="vertical"
          left={leftValue}
        />
        <Box pl={paddingLeftValue}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}